import { Facebook } from "@/assets/icons/Facebook";
import { GoogleAds } from "@/assets/icons/GoogleAds";
import { GoogleAnalytics } from "@/assets/icons/GoogleAnalytics";
import { GoogleTagManager } from "@/assets/icons/GoogleTagManager";
import { Kwai } from "@/assets/icons/Kwai";
import { Taboola } from "@/assets/icons/Taboola";
import { Tiktok } from "@/assets/icons/Tiktok";
import Divider from "@/components/shared/Divider/Divider.tsx";
import { Input } from "@/components/shared/Input/Input";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge.tsx";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import useModalStore from "@/store/useModalStore";
import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "i18next";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { usePixel } from "../hooks/usePixel.ts";

const platforms = {
  facebook: {
    name: "Facebook",
    icon: Facebook,
  },
  google_ads: {
    name: "Google Ads",
    icon: GoogleAds,
  },
  taboola: {
    name: "Taboola",
    icon: Taboola,
  },
  tiktok: {
    name: "TikTok",
    icon: Tiktok,
  },
  kwai: {
    name: "Kwai",
    icon: Kwai,
  },
  google_tag_manager: {
    name: "Google Tag Manager",
    icon: GoogleTagManager,
  },
  google_analytics: {
    name: "Google Analytics",
    icon: GoogleAnalytics,
  },
} as const;

interface EditPixelSheetProps {
  productId: string;
  pixelId: string;
  onUpdate: (data: any) => void;
  isPending: boolean;
}

const formSchema = z
  .object({
    name: z.string().min(1, t("common:required_field")),
    pixelId: z.string().min(1, t("common:required_field")),
    apiToken: z.string().min(1, t("common:required_field")),
    triggerOnPageView: z.boolean(),
    triggerOnInitiateCheckout: z.boolean(),
    triggerOnAddPaymentInfo: z.boolean(),
    triggerOnPurchase: z.boolean(),
    triggerOnPixelGenerated: z.boolean(),
    triggerOnBoletoGenerated: z.boolean(),
    triggerOnCardApproved: z.boolean(),
    status: z.boolean(),
  })
  .refine(
    (data) =>
      data.triggerOnPageView ||
      data.triggerOnInitiateCheckout ||
      data.triggerOnAddPaymentInfo ||
      data.triggerOnPurchase,
    {
      message: t("common:trigger_activation_error"),
      path: ["triggerActivationError"],
    }
  );

type FormData = z.infer<typeof formSchema>;

type TriggerKey = keyof Omit<
  FormData,
  "name" | "pixelId" | "apiToken" | "status"
>;

const baseEventMapping: Record<TriggerKey, string> = {
  triggerOnPageView: "page_view",
  triggerOnInitiateCheckout: "initiate_checkout",
  triggerOnAddPaymentInfo: "add_payment_info",
  triggerOnPurchase: "purchase",
  triggerOnPixelGenerated: "pix_generated",
  triggerOnBoletoGenerated: "bank_slip_generated",
  triggerOnCardApproved: "card_approved",
};

// pixel_events manda a chave canônica de pixel_event_types.key pra todo provider — o backend
// (PixelIntegrationController::update/store) resolve a chave específica de cada provider
// (ex.: "begin_checkout" no Google Ads) via pixel_provider_event_types.provider_event_key,
// não é o client que decide isso. Mandar chave de provider aqui faz o whereIn('key', ...) não
// achar nada e o update() estourar "Pixel events not found". Mesmo padrão do AddPixelSheet.
function getEventMapping(): Record<TriggerKey, string> {
  return baseEventMapping;
}

const orderedTriggerKeys: TriggerKey[] = [
  "triggerOnPageView",
  "triggerOnInitiateCheckout",
  "triggerOnAddPaymentInfo",
  "triggerOnPurchase",
  "triggerOnPixelGenerated",
  "triggerOnBoletoGenerated",
  "triggerOnCardApproved",
];

function isTriggerEnabled(
  events: string[],
  key: TriggerKey,
  mapping: Record<TriggerKey, string>,
) {
  const candidates = new Set<string>([mapping[key], baseEventMapping[key]]);
  if (key === "triggerOnPurchase") candidates.add("approved_purchase");
  return Array.from(candidates).some((v) => events.includes(v));
}

export function EditPixelSheet({
  productId,
  pixelId,
  onUpdate,
  isPending,
}: EditPixelSheetProps) {
  const { t } = useTranslation("pixels");
  const { closeModal } = useModalStore();
  const { pixel, isLoading } = usePixel({ productId, pixelId });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    watch,
    formState: { errors },
    clearErrors,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  useEffect(() => {
    if (pixel) {
      const mapping = getEventMapping();
      const activateEvents: string[] = pixel.pixel_event_types || [];

      setValue("name", pixel.name);
      setValue("pixelId", pixel.pixel_id);
      setValue("apiToken", pixel.pixel_provider_credentials.key_value || "");
      setValue("status", pixel.is_enabled);

      orderedTriggerKeys.forEach((key) => {
        setValue(
          key as keyof FormData,
          isTriggerEnabled(activateEvents, key, mapping),
        );
      });
      // Mantém PageView sempre ativo (não há toggle visível na UI hoje).
      setValue("triggerOnPageView", true);
    }
  }, [pixel, setValue]);

  const handleClose = () => {
    reset();
    closeModal("edit-pixel");
  };

  const onSubmit = (data: FormData) => {
    const mapping = getEventMapping();
    const activateEvents = orderedTriggerKeys
      .filter((key) => (key === "triggerOnPageView" ? true : data[key]))
      .map((key) => mapping[key]);

    const formattedData = {
      name: data.name,
      activate_events: activateEvents,
      pixel_id: data.pixelId,
      pixel_api_token: data.apiToken,
      is_enabled: data.status,
      pixel_events: activateEvents,
      credential: [
        {
          key_name: "pixel_api_token",
          key_value: data.apiToken,
        },
      ],
    };
    onUpdate(formattedData);
  };

  const getPlatformInfo = (integrationType: string) => {
    const platform = platforms[integrationType as keyof typeof platforms];
    if (!platform) {
      return {
        name: integrationType,
        icon: null,
      };
    }
    const Icon = platform.icon;
    return {
      name: platform.name,
      icon: <Icon />,
    };
  };

  if (isLoading) {
    return null;
  }

  const platformInfo = getPlatformInfo(pixel?.pixelProvider || "");
  const currentEventMapping = getEventMapping();
  // Só ajusta copy/texto de ajuda pro Google Ads/Taboola — schema, payload e fluxo dos
  // toggles continuam idênticos pra todos os providers.
  const isGoogleAds = pixel?.pixelProvider === "google_ads";
  // Taboola: "Pixel ID" é o Account ID da conta (não um ID por pixel/produto), e o campo de
  // token é na verdade o Event Name — precisa bater exato (incl. maiúsculas/minúsculas) com o
  // nome cadastrado no Realize (painel da Taboola), senão o evento não é reconhecido.
  const isTaboola = pixel?.pixelProvider === "taboola";

  return (
    <section className="h-full flex flex-col justify-between">
      <div className="w-full mx-auto h-full flex flex-col">
        <div className="flex items-center justify-between px-4 pt-6 pb-4">
          <div className="flex items-center gap-2">
            {platformInfo.icon}
            <span className="text-sm font-normal leading-5 text-left color-[#FAFAFA]">
              {platformInfo.name}
            </span>
          </div>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col flex-1"
        >
          <div className="flex-1 p-4 overflow-y-auto">
            <h3 className="text-lg font-medium text-zinc-100 mb-3">
              {t("edit_pixel")}
            </h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Controller
                  name="status"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Switch
                      checked={value}
                      onCheckedChange={onChange}
                      data-cy="switch-pixel-status"
                    />
                  )}
                />
                <span className="text-sm ">{t("activate_pixel")}</span>
              </div>

              <Input
                label={t("identification_name")}
                placeholder={t("enter_identification_name")}
                data-cy="input-pixel-name"
                {...register("name", { required: true })}
                helperText={errors?.name?.message}
              />

              <Input
                label="Pixel ID"
                placeholder="Ex: 1234567890123456"
                data-cy="input-pixel-id"
                {...register("pixelId", { required: true })}
                helperText={errors?.pixelId?.message}
              />
              {isGoogleAds && (
                <p className="text-xs text-zinc-500 -mt-3">
                  {t("google_ads_pixel_id_hint")}
                </p>
              )}
              {isTaboola && (
                <p className="text-xs text-zinc-500 -mt-3">
                  {t("taboola_pixel_id_hint")}
                </p>
              )}
              <div>
                <label className="block text-sm text-zinc-100">
                  {isGoogleAds && t("google_ads_conversion_label")}
                  {isTaboola && t("taboola_event_name_label")}
                  {!isGoogleAds && !isTaboola && t("api_token")}{" "}
                  <span className="text-zinc-500"></span>
                </label>
                <textarea
                  className="my-1.5 w-full h-[100px] px-3 py-2 bg-transparent border border-zinc-800 rounded-md text-sm text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary resize-none"
                  placeholder={
                    isGoogleAds
                      ? t("google_ads_enter_conversion_label")
                      : isTaboola
                      ? t("taboola_enter_event_name")
                      : t("enter_api_token")
                  }
                  data-cy="input-api-token"
                  {...register("apiToken")}
                />
                {isGoogleAds && (
                  <p className="text-xs text-zinc-500 -mt-1 mb-1.5">
                    {t("google_ads_conversion_label_hint")}
                  </p>
                )}
                {isTaboola && (
                  <p className="text-xs text-zinc-500 -mt-1 mb-1.5">
                    {t("taboola_event_name_hint")}
                  </p>
                )}
                <div className="flex justify-end -mt-2">
                  <span className="text-xs text-zinc-500">
                    {watch("apiToken")?.length}/2000
                  </span>
                </div>
                {errors["apiToken"] && (
                  <p className="text-error text-sm mt-2">
                    {errors["apiToken"].message}
                  </p>
                )}
              </div>

              <Divider />
              <div className="space-y-3">
                <h3 className="text-lg font-medium text-zinc-100 mb-3">
                  {t("activation_environments")}
                </h3>
                {isGoogleAds && (
                  <Alert variant="info">
                    <p className="text-sm">
                      {t("google_ads_shared_label_warning")}
                    </p>
                  </Alert>
                )}
                {isTaboola && (
                  <Alert variant="info">
                    <p className="text-sm">
                      {t("taboola_shared_event_name_warning")}
                    </p>
                  </Alert>
                )}
                {/* <div className="flex items-center gap-3">
                  <Controller
                    name="triggerOnPageView"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onCheckedChange={(checked) => {
                          field.onChange(checked);
                          clearErrors("triggerActivationError" as any);
                        }}
                        data-cy="switch-trigger-on-page_view"
                        className="data-[state=checked]:bg-primary"
                      />
                    )}
                  />
                  <span className="text-sm">
                    {t("trigger_page_view")}{" "}
                    <Badge
                      variant="small"
                      className="text-xs font-medium whitespace-nowrap inline-block"
                    >
                      PageView
                    </Badge>
                  </span>
                </div> */}
                <div className="flex items-center gap-3">
                  <Controller
                    name="triggerOnInitiateCheckout"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onCheckedChange={(checked) => {
                          field.onChange(checked);
                          clearErrors("triggerActivationError" as any);
                        }}
                        data-cy="switch-trigger-on-initiate-checkout"
                        className="data-[state=checked]:bg-primary"
                      />
                    )}
                  />
                  <span className="text-sm">
                    {t("trigger_page_view")}{" "}
                    <Badge
                      variant="small"
                      className="text-xs font-medium whitespace-nowrap inline-block"
                    >
                      {currentEventMapping.triggerOnInitiateCheckout}
                    </Badge>
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Controller
                    name="triggerOnAddPaymentInfo"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onCheckedChange={(checked) => {
                          field.onChange(checked);
                          clearErrors("triggerActivationError" as any);
                        }}
                        data-cy="switch-trigger-on-add-payment-info"
                        className="data-[state=checked]:bg-primary"
                      />
                    )}
                  />
                  <span className="text-sm">
                    {t("trigger_add_payment_info")}{" "}
                    <Badge
                      variant="small"
                      className="text-xs font-medium whitespace-nowrap inline-block"
                    >
                      {currentEventMapping.triggerOnAddPaymentInfo}
                    </Badge>
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Controller
                    name="triggerOnPurchase"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onCheckedChange={(checked) => {
                          field.onChange(checked);
                          clearErrors("triggerActivationError" as any);
                        }}
                        data-cy="switch-trigger-on-purchase"
                        className="data-[state=checked]:bg-primary"
                      />
                    )}
                  />
                  <span className="text-sm">{t("trigger_on_purchase")}</span>
                  <Badge
                    variant="small"
                    className="text-xs font-medium whitespace-nowrap inline-block"
                  >
                    {currentEventMapping.triggerOnPurchase}
                  </Badge>
                </div>
              </div>
              {errors["triggerActivationError"] && (
                <p className="text-error text-sm mt-2">
                  {errors["triggerActivationError"].message}
                </p>
              )}
              {/* <Divider />
              <div className="space-y-3">
                <h3 className="text-lg font-medium text-zinc-100 mb-3">
                  {t("activation_conditions")}
                </h3>
                <div className="flex items-center gap-3">
                  <Controller
                    name="triggerOnPixelGenerated"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onCheckedChange={(checked) => {
                          field.onChange(checked);
                          clearErrors("triggerConditionError" as any);
                        }}
                        data-cy="switch-trigger-on-pix"
                      />
                    )}
                  />
                  <span className="text-sm ">{t("pixel_generated")}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Controller
                    name="triggerOnBoletoGenerated"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onCheckedChange={(checked) => {
                          field.onChange(checked);
                          clearErrors("triggerConditionError" as any);
                        }}
                        data-cy="switch-trigger-on-boleto"
                      />
                    )}
                  />
                  <span className="text-sm">{t("boleto_generated")}</span>
                </div>
              </div> */}
              {errors["triggerConditionError"] && (
                <p className="text-error text-sm mt-2">
                  {errors["triggerConditionError"].message}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end p-4 border-t max-sm:pb-12">
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              className="text-zinc-400 hover:text-zinc-100"
            >
              {t("cancel")}
            </Button>
            <Button
              type="submit"
              disabled={isPending}
              data-cy="btn-save-pixel"
              className="bg-primary text-black hover:bg-primary/90"
            >
              {t("save_changes")}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
}
